import { useEffect, useMemo, useState } from 'react';
import { inpatientsServices } from '../services/endpoints/inpatients';
import { SourceState } from '../types';
import { DemographicPatient, demographicsState, toDemographicPatients } from '../utils/demographics';
import { useDecryptedPatients } from './useDecryptedPatients';

interface UseInpatientHubDataParams {
  investigation: { uuid: string };
  departmentSelected: string;
  startDate: number | null;
  endDate: number | null;
}

export const useInpatientHubData = ({
  investigation,
  departmentSelected,
  startDate,
  endDate,
}: UseInpatientHubDataParams) => {
  const [currentSituation, setCurrentSituation] = useState<any>(null);
  const [periodActivity, setPeriodActivity] = useState<any>(null);
  const [loadingCurrent, setLoadingCurrent] = useState(true);
  const [loadingPeriod, setLoadingPeriod] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  // SHER-897. The demographics section is derived from the period-activity response rather than
  // fetched on its own. That is the ticket's requirement, not a shortcut: a second request is a
  // second moment, and the tile and the section would be free to disagree again.
  const { patients: patientsData, settled: patientsSettled } = useDecryptedPatients(
    investigation?.uuid,
    true,
  );

  useEffect(() => {
    const abortController = new AbortController();
    const uuidDepartment = departmentSelected || 'all';

    if (!investigation?.uuid) {
      return undefined;
    }

    setLoadingCurrent(true);
    inpatientsServices.getCurrentSituation(
      investigation.uuid,
      uuidDepartment,
      abortController.signal,
    )
      .then((data) => {
        if (!abortController.signal.aborted) {
          setCurrentSituation(data);
        }
      })
      .catch((err) => {
        if (!abortController.signal.aborted) {
          setError(err instanceof Error ? err : new Error('Failed to load current inpatient situation'));
        }
      })
      .finally(() => {
        if (!abortController.signal.aborted) {
          setLoadingCurrent(false);
        }
      });

    return () => abortController.abort();
  }, [investigation?.uuid, departmentSelected]);

  useEffect(() => {
    const abortController = new AbortController();
    const uuidDepartment = departmentSelected || 'all';

    if (!investigation?.uuid || !startDate || !endDate) {
      return undefined;
    }

    setLoadingPeriod(true);
    inpatientsServices.getPeriodActivity(
      investigation.uuid,
      uuidDepartment,
      startDate,
      endDate,
      abortController.signal,
    )
      .then((data) => {
        if (!abortController.signal.aborted) {
          setPeriodActivity(data);
        }
      })
      .catch((err) => {
        if (!abortController.signal.aborted) {
          setError(err instanceof Error ? err : new Error('Failed to load inpatient period activity'));
        }
      })
      .finally(() => {
        if (!abortController.signal.aborted) {
          setLoadingPeriod(false);
        }
      });

    return () => abortController.abort();
  }, [investigation?.uuid, departmentSelected, startDate, endDate]);

  const patientIds: number[] | undefined = periodActivity?.stats?.patients?.ids;
  const firstAdmissionDates = periodActivity?.stats?.patients?.firstAdmissionDates;

  const demographics: DemographicPatient[] = useMemo(
    () => toDemographicPatients(patientIds, firstAdmissionDates, patientsData),
    [patientIds, firstAdmissionDates, patientsData],
  );

  // Two inputs that land independently — this response and the redux decrypt — so the card has
  // to be able to say "not yet" and "not coming", not just "here".
  const demographicsStatus: SourceState = useMemo(() => {
    if (loadingPeriod) return { status: 'pending' };
    // No caller-side "did we get an answer" branch: `patientIds` being undefined IS that
    // answer, and `demographicsState` owns what it means. A branch here was both redundant and
    // weaker — it tested `stats`, so a response carrying `stats` without a `patients` block (an
    // API older than this client) walked straight past it.
    //
    // The hook's `error` is deliberately not consulted either: it is shared with the
    // current-situation request, so reading it would blame this card for that one's failure.
    return demographicsState(patientIds, patientsData, patientsSettled);
  }, [loadingPeriod, patientIds, patientsData, patientsSettled]);

  return {
    currentSituation,
    periodActivity,
    demographics,
    demographicsStatus,
    loadingCurrent,
    loadingPeriod,
    error,
  };
};
